import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { BaseButton } from '@cks-internal/react-base';
import { useState } from 'react';
import { QuantityPicker } from '../../shoppingCart/components/QuantityPicker.tsx';

type PackSizeSelectorProps = {
  packSizes: number[];
  minimumQuantity?: number;
  value: number;
  onChange: (value: number) => void;
};

export const PackSizeSelector = (props: PackSizeSelectorProps) => {
  const { packSizes, minimumQuantity = 1, value, onChange } = props;
  const [isCustom, setIsCustom] = useState(false);

  // Sort pack sizes in ascending order
  const sortedPackSizes = [...packSizes].sort((a, b) => a - b);

  // Check if current value matches a pack size
  const isValueAPackSize = sortedPackSizes.includes(value);

  // Determine if we should show custom mode
  const showCustomInput = isCustom || (!isValueAPackSize && value !== sortedPackSizes[0]);

  const handlePackSizeClick = (size: number) => {
    setIsCustom(false);
    onChange(size);
  };

  const handleCustomClick = () => {
    setIsCustom(true);
    // Ensure value is at least minimum when switching to custom
    if (value < minimumQuantity) {
      onChange(minimumQuantity);
    }
  };

  const handleCustomQuantityChange = (newValue: number) => {
    onChange(newValue);
  };

  // If no pack sizes defined, just show the quantity picker
  if (sortedPackSizes.length === 0) {
    return (
      <QuantityPicker
        min={minimumQuantity}
        max={999}
        value={value}
        defaultValue={minimumQuantity}
        onChange={handleCustomQuantityChange}
      />
    );
  }

  return (
    <VStack align='stretch' gap={3}>
      <Flex flexWrap='wrap' gap={2}>
        {sortedPackSizes.map((size) => (
          <BaseButton
            key={size}
            size='sm'
            variant={value === size && !showCustomInput ? 'solid' : 'outline'}
            colorPalette={value === size && !showCustomInput ? 'blue' : 'gray'}
            onClick={() => handlePackSizeClick(size)}>
            {size}
          </BaseButton>
        ))}
        <BaseButton
          size='sm'
          variant={showCustomInput ? 'solid' : 'outline'}
          colorPalette={showCustomInput ? 'blue' : 'gray'}
          onClick={handleCustomClick}>
          Custom
        </BaseButton>
      </Flex>

      {showCustomInput && (
        <Box>
          <Text fontSize='sm' color='gray.600' mb={1}>
            Enter custom quantity:
          </Text>
          <HStack>
            <QuantityPicker
              min={minimumQuantity}
              max={999}
              value={value}
              defaultValue={minimumQuantity}
              onChange={handleCustomQuantityChange}
            />
          </HStack>
        </Box>
      )}
    </VStack>
  );
};
